import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Database,
  HardDrive,
  History,
  Loader2,
  RefreshCw,
  Trash2,
  Users,
  Zap,
} from "lucide-react";
import ConfirmDialog from "../common/ConfirmDialog";
import { useCaseCommand } from "hooks/useCaseDashboard";
import {
  formatBytes,
  isRefusedCommand,
  SESSION_ID_RE,
} from "utils/caseDashboard";
import {
  Card,
  EmptyState,
  Notice,
  SecondaryButton,
  fmtDateTime,
} from "./parts";

// Disk space on the case and the sessions in its database, with delete
// (case software v0.9.0+: `storage`, `sessions`, `session-delete <id>`).
// An older case refuses these commands; the view says so instead.

const NEEDS = "v0.9.0";

// The `storage` items the case reports, in its order. Unknown keys show raw.
const ITEM_KEYS = [
  "database",
  "impact_files",
  "uploaded_files",
  "docker_images",
  "docker_build_cache",
  "docker_volumes",
  "releases",
];

/** A `@@JSON` command's error, or null: transport errors and `ok: false`. */
const commandError = (query) =>
  query.data?.ok === false
    ? query.data.error || "error"
    : !query.data && query.error
    ? query.error
    : null;

const StorageView = ({ caseId }) => {
  const { t } = useTranslation();
  const storage = useCaseCommand(caseId, "storage", 0);
  const sessions = useCaseCommand(caseId, "sessions", 30000);
  const refused =
    isRefusedCommand(storage.error) || isRefusedCommand(sessions.error);

  const refreshAll = () => {
    storage.refresh();
    sessions.refresh();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-[#111827]">
            {t("caseDashboard.tabs.storage")}
          </h1>
          <p className="mt-0.5 text-[13px] text-[#6B7280]">
            {t("caseDashboard.storage.subtitle")}
          </p>
        </div>
        {!refused && (
          <SecondaryButton onClick={refreshAll}>
            <RefreshCw size={14} />
            {t("caseDashboard.storage.refresh")}
          </SecondaryButton>
        )}
      </div>

      {refused ? (
        <Card>
          <EmptyState
            icon={HardDrive}
            title={t("caseDashboard.storage.needsTitle")}
            body={t("caseDashboard.common.needsVersion", { version: NEEDS })}
          />
        </Card>
      ) : (
        <>
          <DiskCard storage={storage} />
          <SessionsCard
            caseId={caseId}
            sessions={sessions}
            onDeleted={refreshAll}
          />
        </>
      )}
    </div>
  );
};

const DiskCard = ({ storage }) => {
  const { t } = useTranslation();
  const data = storage.data?.ok === false ? null : storage.data;
  const error = commandError(storage);
  const disk = data?.disk || {};
  const total = Number(disk.total_bytes) || 0;
  const used = Number(disk.used_bytes) || 0;
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  const items = data?.items || [];

  return (
    <Card title={t("caseDashboard.storage.diskTitle")}>
      <div className="p-4">
        {error && (
          <Notice tone="error">
            {t("caseDashboard.common.queryError", { message: error })}
          </Notice>
        )}
        {!data && !error && (
          <p className="text-[13px] text-[#6B7280]">
            {t("caseDashboard.common.loading")}
          </p>
        )}
        {data && (
          <>
            <div className="flex items-end justify-between gap-4">
              <p className="text-[22px] font-bold tabular-nums leading-none text-[#111827]">
                {formatBytes(disk.free_bytes)}
                <span className="ml-1.5 text-[13px] font-medium text-[#6B7280]">
                  {t("caseDashboard.storage.free")}
                </span>
              </p>
              <p className="text-[12px] text-[#6B7280]">
                {t("caseDashboard.storage.usedOf", {
                  used: formatBytes(disk.used_bytes),
                  total: formatBytes(disk.total_bytes),
                })}
              </p>
            </div>
            <div
              className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#F3F4F6]"
              role="meter"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(pct)}
              aria-label={t("caseDashboard.storage.diskTitle")}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: pct > 90 ? "#DC2626" : "#E07A3A",
                }}
              />
            </div>
          </>
        )}
      </div>
      {items.length > 0 && (
        <ul className="border-t border-[#F3F4F6]">
          {items.map((item) => {
            const known = ITEM_KEYS.includes(item.key);
            const bytes = Number(item.bytes) || 0;
            return (
              <li
                key={item.key}
                className="flex items-center gap-4 border-b border-[#F3F4F6] px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-[#111827]">
                    {known
                      ? t(`caseDashboard.storage.items.${item.key}.title`)
                      : item.key}
                  </p>
                  {known && (
                    <p className="text-[12px] text-[#6B7280]">
                      {t(`caseDashboard.storage.items.${item.key}.about`)}
                    </p>
                  )}
                </div>
                <div className="hidden w-28 sm:block">
                  {used > 0 && item.bytes != null && (
                    <div className="h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
                      <div
                        className="h-full rounded-full bg-[#9CA3AF]"
                        style={{
                          width: `${Math.min(100, (bytes / used) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="w-28 shrink-0 text-right">
                  <p className="text-[13px] font-semibold tabular-nums text-[#111827]">
                    {formatBytes(item.bytes)}
                  </p>
                  {item.files != null && (
                    <p className="text-[11px] text-[#9CA3AF]">
                      {t("caseDashboard.storage.fileCount", {
                        count: Number(item.files) || 0,
                      })}
                    </p>
                  )}
                  {item.reclaimable && (
                    <p className="text-[11px] text-[#9CA3AF]">
                      {t("caseDashboard.storage.reclaimable", {
                        size: item.reclaimable,
                      })}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

const SessionsCard = ({ caseId, sessions, onDeleted }) => {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState(null); // the session to delete
  const [deleting, setDeleting] = useState(null);
  const [last, setLast] = useState(null);
  const data = sessions.data?.ok === false ? null : sessions.data;
  const error = commandError(sessions);
  const rows = data?.sessions || [];

  const remove = async (session) => {
    setConfirm(null);
    setLast(null);
    setDeleting(session.id);
    const res = await window.electronAPI?.caseDashboard?.run(
      caseId,
      `session-delete ${session.id}`
    );
    setDeleting(null);
    const json = res?.json;
    if (res?.ok && json?.ok !== false) {
      setLast({ ok: true, session, ...json });
    } else {
      const message = json?.error || res?.error?.message || res?.error || "";
      setLast({
        ok: false,
        session,
        error: isRefusedCommand(message)
          ? t("caseDashboard.common.needsVersion", { version: NEEDS })
          : message,
      });
    }
    onDeleted();
  };

  return (
    <Card
      title={t("caseDashboard.storage.sessionsTitle")}
      action={
        data?.database_bytes != null && (
          <span className="flex items-center gap-1.5 text-[12px] text-[#6B7280]">
            <Database size={13} className="text-[#9CA3AF]" />
            {t("caseDashboard.storage.databaseSize", {
              size: formatBytes(data.database_bytes),
            })}
          </span>
        )
      }
    >
      {last && (
        <div className="border-b border-[#F3F4F6] p-4">
          <DeleteResult result={last} />
        </div>
      )}
      {error && (
        <div className="p-4">
          <Notice tone="error">
            {t("caseDashboard.common.queryError", { message: error })}
          </Notice>
        </div>
      )}
      {rows.length === 0 ? (
        !error && (
          <EmptyState
            icon={History}
            title={
              sessions.loading
                ? t("caseDashboard.common.loading")
                : t("caseDashboard.storage.noSessions")
            }
          />
        )
      ) : (
        <ul>
          {rows.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-4 border-b border-[#F3F4F6] px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
                  {fmtDateTime(s.started_at)}
                  {s.current && (
                    <span className="rounded-full bg-[#DCFCE7] px-1.5 py-0.5 text-[10px] font-semibold text-[#15803D]">
                      {t("caseDashboard.history.running")}
                    </span>
                  )}
                </p>
                <p className="truncate text-[12px] text-[#6B7280]">
                  {s.current
                    ? s.id
                    : s.ended_at
                    ? t("caseDashboard.storage.ended", {
                        time: fmtDateTime(s.ended_at),
                      })
                    : t("caseDashboard.history.abandoned")}
                </p>
              </div>
              <span className="flex w-28 items-center gap-1.5 text-[12px] text-[#6B7280]">
                <Users size={13} className="text-[#9CA3AF]" />
                {t("caseDashboard.storage.wearableCount", {
                  count: Number(s.wearables) || 0,
                })}
              </span>
              <span className="flex w-28 items-center gap-1.5 text-[12px] text-[#6B7280]">
                <Zap size={13} className="text-[#9CA3AF]" />
                {t("caseDashboard.history.impactCount", {
                  count: Number(s.impacts) || 0,
                })}
              </span>
              <span className="flex w-24 justify-end">
                {!s.current && SESSION_ID_RE.test(String(s.id)) && (
                  <button
                    type="button"
                    onClick={() => setConfirm(s)}
                    disabled={!!deleting}
                    aria-label={t("caseDashboard.storage.deleteSession", {
                      time: fmtDateTime(s.started_at),
                    })}
                    className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-medium text-[#B91C1C] transition duration-150 hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleting === s.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Trash2 size={13} />
                    )}
                    {t("caseDashboard.storage.delete")}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t("caseDashboard.storage.confirmTitle")}
        body={t("caseDashboard.storage.confirmBody", {
          time: confirm ? fmtDateTime(confirm.started_at) : "",
          count: Number(confirm?.impacts) || 0,
        })}
        confirmLabel={t("caseDashboard.storage.delete")}
        cancelLabel={t("caseDashboard.controls.cancel")}
        danger
        onConfirm={() => remove(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </Card>
  );
};

/** What a delete removed, and what it did to the database's size. */
const DeleteResult = ({ result }) => {
  const { t } = useTranslation();
  if (!result.ok)
    return (
      <Notice tone="error">
        {t("caseDashboard.storage.deleteFailed", { message: result.error })}
      </Notice>
    );
  const d = result.deleted || {};
  const before = result.database_bytes_before;
  const after = result.database_bytes_after;
  // Postgres keeps freed space for reuse while a session runs.
  const kept = before != null && after != null && after >= before;
  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-[#F9FAFB] p-4">
      <p className="flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
        <CheckCircle2 size={15} className="text-[#059669]" />
        {t("caseDashboard.storage.deleted", {
          time: fmtDateTime(result.session?.started_at),
        })}
      </p>
      <p className="mt-1 text-[12px] text-[#6B7280]">
        {[
          t("caseDashboard.history.impactCount", {
            count: Number(d.impacts) || 0,
          }),
          t("caseDashboard.storage.counts.assignments", {
            count: Number(d.assignments) || 0,
          }),
          t("caseDashboard.storage.counts.syncs", {
            count: Number(d.syncs) || 0,
          }),
          t("caseDashboard.storage.counts.pairingEvents", {
            count: Number(d.pairing_events) || 0,
          }),
        ].join(" · ")}
      </p>
      {(before != null || after != null) && (
        <p className="mt-1 text-[12px] text-[#6B7280]">
          {t("caseDashboard.storage.databaseChange", {
            before: formatBytes(before),
            after: formatBytes(after),
          })}
        </p>
      )}
      {kept && (
        <p className="mt-2 text-[12px] text-[#9A4A17]">
          {t("caseDashboard.storage.spaceLater")}
        </p>
      )}
    </div>
  );
};

export default StorageView;
